if (!customElements.get('lambretta-testimonials-carousel')) {
  class LambrettaTestimonialsCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.lambretta-testimonials__viewport');
      this.track = this.querySelector('.lambretta-testimonials__track');
      const layoutRoot = this.closest('.lambretta-testimonials__layout') || this;
      this.prevButton = layoutRoot.querySelector('[data-direction="previous"]');
      this.nextButton = layoutRoot.querySelector('[data-direction="next"]');

      if (!this.viewport || !this.track) return;

      this.realSlides = Array.from(
        this.track.querySelectorAll('.lambretta-testimonials__slide:not(.lambretta-testimonials__slide--clone)')
      );
      this.slides = this.realSlides;
      this.slideCount = this.realSlides.length;
      this.currentIndex = 0;
      this.autoplayDelay = (Number(this.dataset.autoplayInterval) || 5) * 1000;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slideCount > 1;
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
      this.isAdjustingScroll = false;

      if (this.slideCount < 2) {
        this.classList.add('is-static');
        this.updateButtons();
        return;
      }

      this.onScroll = this.onScroll.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onResize = this.onResize.bind(this);
      this.checkLoopPosition = this.checkLoopPosition.bind(this);

      this.initInfiniteLoop();

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onResize);

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => this.startAutoplay());
      }

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.prefersReducedMotion.addEventListener('change', () => {
        if (this.prefersReducedMotion.matches) this.stopAutoplay();
        else this.startAutoplay();
      });

      this.updateButtons();

      if (!this.prefersReducedMotion.matches) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.loopResizeObserver?.disconnect();
      this.viewport?.removeEventListener('scroll', this.onScroll);
      if ('onscrollend' in window) {
        this.viewport?.removeEventListener('scrollend', this.checkLoopPosition);
      }
      window.removeEventListener('resize', this.onResize);
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.classList.add('lambretta-testimonials__slide--clone');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    }

    initInfiniteLoop() {
      const leadingFragment = document.createDocumentFragment();
      const trailingFragment = document.createDocumentFragment();

      this.realSlides.forEach((slide) => {
        trailingFragment.appendChild(this.createClone(slide));
      });

      for (let index = this.realSlides.length - 1; index >= 0; index -= 1) {
        leadingFragment.appendChild(this.createClone(this.realSlides[index]));
      }

      this.track.insertBefore(leadingFragment, this.track.firstChild);
      this.track.appendChild(trailingFragment);

      if ('onscrollend' in window) {
        this.viewport.addEventListener('scrollend', this.checkLoopPosition);
      }

      requestAnimationFrame(() => {
        this.measureLoopBounds();
        this.repositionScroll(this.firstRealOffset);
        this.syncIndexFromScroll();
      });

      this.loopResizeObserver = new ResizeObserver(() => {
        this.measureLoopBounds();
      });
      this.loopResizeObserver.observe(this.track);
    }

    measureLoopBounds() {
      if (!this.realSlides.length) return;

      const trackStyles = getComputedStyle(this.track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

      this.firstRealOffset = this.realSlides[0].offsetLeft;
      this.lastRealOffset = this.realSlides[this.slideCount - 1].offsetLeft;
      this.loopDistance = this.firstRealOffset - this.track.firstElementChild.offsetLeft;
      this.slideStep = this.realSlides[0].offsetWidth + gap;
    }

    repositionScroll(scrollLeft) {
      this.isAdjustingScroll = true;
      this.viewport.style.setProperty('scroll-behavior', 'auto', 'important');
      this.viewport.scrollLeft = scrollLeft;

      requestAnimationFrame(() => {
        this.viewport.style.removeProperty('scroll-behavior');
        this.isAdjustingScroll = false;
      });
    }

    checkLoopPosition() {
      if (this.isAdjustingScroll || !this.loopDistance || !this.slideStep) return;

      const { scrollLeft } = this.viewport;
      const trailingThreshold = this.lastRealOffset + this.slideStep * 0.25;
      const leadingThreshold = this.firstRealOffset - this.slideStep * 0.25;

      if (scrollLeft >= trailingThreshold) {
        this.repositionScroll(scrollLeft - this.loopDistance);
      } else if (scrollLeft <= leadingThreshold) {
        this.repositionScroll(scrollLeft + this.loopDistance);
      }

      this.syncIndexFromScroll();
    }

    onResize() {
      this.measureLoopBounds();
      this.syncIndexFromScroll();
    }

    onScroll() {
      if (this.isAdjustingScroll) return;

      window.clearTimeout(this.scrollSyncTimeout);
      this.scrollSyncTimeout = window.setTimeout(() => {
        this.checkLoopPosition();
        this.syncIndexFromScroll();
      }, 80);
    }

    getScrollOffsetForSlide(index) {
      const slide = this.realSlides[index];
      if (!slide) return 0;
      return Math.max(0, slide.offsetLeft);
    }

    syncIndexFromScroll() {
      const scrollLeft = this.viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.realSlides.forEach((slide, index) => {
        const distance = Math.abs(this.getScrollOffsetForSlide(index) - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.currentIndex = closestIndex;
    }

    scrollByStep(direction, behavior = 'smooth') {
      if (!this.slideStep) return;

      const reducedMotion = this.prefersReducedMotion?.matches;
      const scrollBehavior = reducedMotion ? 'auto' : behavior;

      this.viewport.scrollBy({
        left: direction * this.slideStep,
        behavior: scrollBehavior,
      });
    }

    onPrev(event) {
      event.preventDefault();
      this.stopAutoplay();
      this.scrollByStep(-1);
    }

    onNext(event) {
      event.preventDefault();
      this.stopAutoplay();
      this.scrollByStep(1);
    }

    updateButtons() {
      if (!this.prevButton || !this.nextButton) return;
      const disableNav = this.slideCount < 2;
      this.prevButton.disabled = disableNav;
      this.nextButton.disabled = disableNav;
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.prefersReducedMotion?.matches) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        this.scrollByStep(1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('lambretta-testimonials-carousel', LambrettaTestimonialsCarousel);
}
